import { Box, Stack, Typography } from '@mui/material';
import { useCallback } from 'react';

import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { XCloseIcon } from '@/components/icons/XCloseIcon';
import { SideDrawer } from '@/modules/common/SideDrawer/SideDrawer';
import { HouseholdDocumentsViewerProvider } from '@/modules/documents/components/HouseholdDocumentsViewer/context/HouseholdDocumentsViewer.provider';
import { EntityDocumentViewer_DocumentFragment } from '@/modules/documents/components/HouseholdDocumentsViewer/graphql/EntityDocumentViewer.generated';
import { HouseholdDocumentsViewer } from '@/modules/documents/components/HouseholdDocumentsViewer/HouseholdDocumentsViewer';
import { orderDocumentsByEntityRelationship } from '@/modules/documents/documentRelationships.utils';
import { COLORS } from '@/styles/tokens/colors';
import { OVERLAY_LG } from '@/styles/tokens/panelWidths';

interface ViewSourceDrawerProps {
  householdId: string;
  entityId: string;
  onClose: () => void;
}

/**
 * Side drawer that shows all explicitly associated documents for an entity,
 * regardless of relationship type, using the shared
 * `HouseholdDocumentsViewer` (document/summary toggle, document selector,
 * zoom, search, and an "Open in new tab" control all come from the viewer).
 * Mounted outside the slide canvas's `CustomerThemeProvider` so it renders in
 * the Luminary app theme.
 *
 * Entities only, despite the viewer's generic-looking `targetId` prop: it
 * branches on the target's pulid kind and has no branch for a client
 * organization, so an org id makes it throw during render. Individuals do have a
 * branch, but it lists household-wide wills/POAs filtered by document type
 * rather than anything linked to that person — not a meaningful "source". Both
 * are routed to the suggestion-evidence drawer instead; see
 * `ViewSourceRequest`.
 */
export function ViewSourceDrawer({
  householdId,
  entityId,
  onClose,
}: ViewSourceDrawerProps) {
  const orderDocuments = useCallback(
    (docs: EntityDocumentViewer_DocumentFragment[]) =>
      orderDocumentsByEntityRelationship(docs, entityId),
    [entityId]
  );

  return (
    <SideDrawer isOpen onClose={onClose} width={OVERLAY_LG} anchor="right">
      <HouseholdDocumentsViewerProvider
        householdId={householdId}
        targetId={entityId}
        documentOrderFn={orderDocuments}
      >
        <Stack height="100%" minWidth={0}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ p: 2, borderBottom: `1px solid ${COLORS.GRAY[200]}` }}
          >
            <Typography variant="title-xl">Source documents</Typography>
            <IconButton
              ariaLabel="Close"
              icon={XCloseIcon}
              variant="transparent"
              size="sm"
              onClick={onClose}
            />
          </Stack>
          <Box flex={1} minHeight={0}>
            <HouseholdDocumentsViewer shouldShowOpenInNewTab />
          </Box>
        </Stack>
      </HouseholdDocumentsViewerProvider>
    </SideDrawer>
  );
}
